// Cart drawer / cart page helpers.
// - Estimated arrival: same rule as the ring page (today + made-to-order + transit days), shown in the
//   visitor's time zone as en-US "Oct 30". Server text is "%b %-d" in the shop's time zone.
// - "How it works" photo prints toggle; the open state survives Section Rendering API re-renders.
// Section re-renders morph the markup back to server state, so both are re-applied after DOM changes.

let howtoOpen = false;
let queued = false;

function daysFromToday(days) {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + Number(days || 0));
  return date;
}

export function arrivalText(template, makeDays, transitDays) {
  const date = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(
    daysFromToday(Number(makeDays) + Number(transitDays))
  );
  return String(template || '').replace('{date}', date);
}

function sync() {
  queued = false;
  document.querySelectorAll('[data-aiovo-eta]').forEach((node) => {
    const title = node.querySelector('[data-aiovo-eta-title]');
    if (!title) return;
    const text = arrivalText(node.dataset.title, node.dataset.makeDays, node.dataset.transitDays);
    if (title.textContent !== text) title.textContent = text;
  });
  document.querySelectorAll('[data-aiovo-howto]').forEach((button) => {
    const panel = document.getElementById(button.getAttribute('aria-controls') || '');
    const expanded = String(howtoOpen);
    if (button.getAttribute('aria-expanded') !== expanded) button.setAttribute('aria-expanded', expanded);
    const label = howtoOpen ? button.dataset.labelClose : button.dataset.labelOpen;
    if (label && button.textContent !== label) button.textContent = label;
    if (panel && panel.hidden === howtoOpen) panel.hidden = !howtoOpen;
  });
}

function schedule() {
  if (queued) return;
  queued = true;
  queueMicrotask(sync);
}

document.addEventListener('click', (event) => {
  const button = event.target.closest?.('[data-aiovo-howto]');
  if (!button) return;
  howtoOpen = button.getAttribute('aria-expanded') !== 'true';
  sync();
});

new MutationObserver((records) => {
  scheduleCartMarketing();
  scheduleFlowChat();
  for (const record of records) {
    const target = record.target instanceof Element ? record.target : record.target.parentElement;
    if (target?.closest?.('cart-items-component, #cart-drawer')) {
      schedule();
      queueMicrotask(syncCartSupport);
      return;
    }
    if ([...record.addedNodes, ...record.removedNodes].some((node) => node instanceof Element &&
      (node.matches('shopify-chat, #ads-agent-host') || node.querySelector('shopify-chat, #ads-agent-host')))) queueMicrotask(syncCartSupport);
  }
}).observe(document.documentElement, {
  subtree: true,
  childList: true,
  characterData: true,
  attributes: true,
  attributeFilter: ['aria-expanded', 'hidden', 'data-title', 'open', 'class', 'data-testid', 'data-kl-scroll-locking-modal'],
});

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') schedule();
});
sync();

// Cart surfaces dock both support entries at every viewport width, including
// collapsed AI entries. The applications still own their viewport panels.
// Mobile ring entries follow a reserved row; no polling, app settings or private API.
const supportHomes = new Map();
const ringSupportMedia = matchMedia('(max-width: 989px)');
let renderingCart = false;
let agentShadow;
let ringAgentFrame;
let flowChatFrame;
const agentEntryStyle = `
  :host([data-aiovo-cart-docked]) [data-testid="entrypoint"] {
    position: relative !important;
    inset: auto !important;
    transform: none !important;
    translate: none !important;
    margin: 0 0 0 auto !important;
    max-width: 100% !important;
    width: fit-content !important;
  }
  :host([data-aiovo-ring-docked]) [data-testid="entrypoint"] {
    position: fixed !important;
    top: var(--aiovo-ring-agent-top) !important;
    right: var(--aiovo-ring-agent-right) !important;
    bottom: auto !important;
    margin: 0 !important;
    transform: none !important;
    translate: none !important;
    max-width: var(--aiovo-ring-agent-width) !important;
    /* The vendor's transition-all must not animate scroll-following coordinates. */
    transition-property: opacity !important;
  }
  /* The vendor can leave this wrapper opacity-0 at mobile widths. Keep only
     a rendered flow entry or explicitly opened panel painted. */
  :host([data-aiovo-ring-docked]) #root > div:has([data-testid="entrypoint"], [data-testid="composer"]) {
    opacity: 1 !important;
  }
  :host([data-aiovo-ring-docked]) #root .fixed:has(button[aria-label="Close"]) {
    inset: 16px 16px 16px auto !important;
    width: calc(100vw - 32px) !important;
    max-width: 480px !important;
    height: auto !important;
    max-height: calc(100dvh - 32px) !important;
    /* Retain a containing block for the app's fixed composer. */
    transform: translate(0, 0) !important;
  }
`;

// Reserve an ordinary flow row, but leave the agent host in its original parent:
// relocating this vendor's host can create a second React root. Only its collapsed
// entry follows the row; the app still owns its expanded panel and dismiss state.
function syncRingAgent() {
  ringAgentFrame = null;
  const host = document.querySelector('#ads-agent-host[data-aiovo-ring-docked]');
  const slot = document.querySelector('[data-aiovo-ring-agent-slot]');
  if (!host || !slot) return;
  const entry = host.shadowRoot?.querySelector('[data-testid="entrypoint"]');
  const height = entry ? entry.getBoundingClientRect().height : 0;
  if (slot.style.height !== `${height}px`) slot.style.height = `${height}px`;
  const rect = slot.getBoundingClientRect();
  host.style.setProperty('--aiovo-ring-agent-top', `${rect.top}px`);
  host.style.setProperty('--aiovo-ring-agent-right', `${innerWidth - rect.right}px`);
  host.style.setProperty('--aiovo-ring-agent-width', `${rect.width}px`);
}

function scheduleRingAgent() {
  if (!ringAgentFrame) ringAgentFrame = requestAnimationFrame(syncRingAgent);
}
window.addEventListener('scroll', scheduleRingAgent, { passive: true, capture: true });
window.addEventListener('resize', scheduleRingAgent);

// Inbox's layout containment makes its fixed dialog relative to the flow host.
// Offset the exported dialog part into the visible viewport without moving the
// host, changing its collapsed row, or touching the drawer's separate rules.
function syncFlowChat() {
  flowChatFrame = null;
  const viewport = window.visualViewport;
  const width = viewport?.width || innerWidth;
  const height = viewport?.height || innerHeight;
  const top = (viewport?.offsetTop || 0) + height / 2;
  document.querySelectorAll('shopify-chat[data-aiovo-flow-chat]').forEach((host) => {
    const rect = host.getBoundingClientRect();
    const values = {
      '--aiovo-flow-chat-top': `${top - rect.top}px`,
      '--aiovo-flow-chat-width': `${width}px`,
      '--aiovo-flow-chat-height': `${height}px`,
    };
    for (const [name, value] of Object.entries(values)) {
      if (host.style.getPropertyValue(name) !== value) host.style.setProperty(name, value);
    }
  });
}

function scheduleFlowChat() {
  if (!flowChatFrame) flowChatFrame = requestAnimationFrame(syncFlowChat);
}
window.addEventListener('scroll', scheduleFlowChat, { passive: true, capture: true });
window.addEventListener('resize', scheduleFlowChat);
window.visualViewport?.addEventListener('resize', scheduleFlowChat);
window.visualViewport?.addEventListener('scroll', scheduleFlowChat);

function moveSupport(node, parent, before = null) {
  if (!parent || node.parentNode === parent) return;
  // Preserve custom element state, open panels and focused shadow controls.
  if (parent.moveBefore && node.isConnected) parent.moveBefore(node, before);
  else parent.insertBefore(node, before);
}

function supportSlot() {
  const drawerItems = document.querySelector('#cart-drawer[open] .cart-drawer__items');
  if (drawerItems) {
    let slot = drawerItems.querySelector('[data-aiovo-cart-chat-slot]');
    if (!slot) {
      slot = document.createElement('div');
      slot.className = 'aiovo-cart-chat-slot';
      slot.setAttribute('data-aiovo-cart-chat-slot', '');
      drawerItems.append(slot);
    }
    return slot;
  }
  const pageSlot = document.querySelector('.aiovo-cart-summary--page [data-aiovo-cart-chat-slot]');
  if (pageSlot) return pageSlot;
  const ringBuy = ringSupportMedia.matches && document.querySelector('ring-main .ring-buy');
  if (ringBuy) {
    let slot = ringBuy.querySelector('[data-aiovo-ring-support]');
    if (!slot) {
      slot = document.createElement('div');
      slot.className = 'aiovo-cart-chat-slot';
      slot.setAttribute('data-aiovo-cart-chat-slot', '');
      slot.setAttribute('data-aiovo-ring-support', '');
      ringBuy.querySelector('.ring-buy__share').after(slot);
      new ResizeObserver(scheduleRingAgent).observe(ringBuy);
    }
    return slot;
  }
  const emptyPage = document.querySelector('.aiovo-cart-empty--page');
  if (!emptyPage) return null;
  let slot = emptyPage.querySelector('[data-aiovo-cart-chat-slot]');
  if (!slot) {
    slot = document.createElement('div');
    slot.className = 'aiovo-cart-chat-slot';
    slot.setAttribute('data-aiovo-cart-chat-slot', '');
    emptyPage.append(slot);
  }
  return slot;
}

function syncCartSupport() {
  if (renderingCart) return;
  const slot = supportSlot();
  let activeRingSlot;
  for (const [node, home] of supportHomes) {
    if (!node.isConnected) { home.remove(); supportHomes.delete(node); }
  }
  for (const selector of ['shopify-chat', '#ads-agent-host']) {
    const node = document.querySelector(selector);
    if (!node) continue;
    if (!supportHomes.has(node)) {
      const home = document.createComment('aiovo-cart-support-home');
      node.before(home);
      supportHomes.set(node, home);
    }
    const home = supportHomes.get(node);
    if (node.id === 'ads-agent-host' && node.shadowRoot && agentShadow !== node.shadowRoot) {
      agentShadow = node.shadowRoot;
      const style = document.createElement('style');
      style.setAttribute('data-aiovo-cart-support-style', '');
      style.textContent = agentEntryStyle;
      agentShadow.append(style);
      new MutationObserver(scheduleRingAgent).observe(agentShadow, { childList: true, subtree: true, attributes: true });
    }
    const ringAgent = node.id === 'ads-agent-host' && slot?.hasAttribute('data-aiovo-ring-support');
    node.toggleAttribute('data-aiovo-flow-chat', node.localName === 'shopify-chat' &&
      Boolean(slot && !slot.closest('#cart-drawer')));
    node.toggleAttribute('data-aiovo-ring-docked', Boolean(ringAgent));
    if (ringAgent) {
      let row = slot.querySelector('[data-aiovo-ring-agent-slot]');
      if (!row) {
        row = document.createElement('div');
        row.setAttribute('data-aiovo-ring-agent-slot', '');
        row.style.width = '100%';
        slot.append(row);
      }
      activeRingSlot = row;
      node.removeAttribute('data-aiovo-cart-docked');
      moveSupport(node, home.parentNode, home.nextSibling);
      scheduleRingAgent();
      continue;
    }
    node.toggleAttribute('data-aiovo-cart-docked', Boolean(slot));
    moveSupport(node, slot || home.parentNode, slot ? null : home.nextSibling);
    if (node.hasAttribute('data-aiovo-flow-chat')) scheduleFlowChat();
  }
  // The mobile AI host stays at home, so its active placeholder also owns the
  // row. Remove stale placeholders when the host disappears or changes surface.
  document.querySelectorAll('[data-aiovo-ring-agent-slot]').forEach((row) => {
    if (row !== activeRingSlot) row.remove();
  });
  document.querySelectorAll('[data-aiovo-cart-chat-slot]').forEach((row) => {
    const hidden = !row.querySelector('shopify-chat, #ads-agent-host') &&
      !(activeRingSlot && row.contains(activeRingSlot));
    if (row.hidden !== hidden) row.hidden = hidden;
  });
}

// Evacuate app-owned hosts before a section morph removes their placeholder.
// Reattach once, after the whole render, even when rendering fails.
document.addEventListener('aiovo:cart-render-start', () => {
  renderingCart = true;
  for (const [node, home] of supportHomes) moveSupport(node, home.parentNode, home.nextSibling);
});
document.addEventListener('aiovo:cart-render-end', () => {
  renderingCart = false;
  syncCartSupport();
});
window.addEventListener('pageshow', syncCartSupport);
ringSupportMedia.addEventListener('change', syncCartSupport);
syncCartSupport();

// Pause only the confirmed welcome form. Keep its application state and dismissal
// rules intact: no close/open API, storage change, node deletion or subscription.
const welcomeForm = '[data-testid="klaviyo-form-Vuw7YY"]';
const welcomeTeaser = '.kl-teaser-Vuw7YY';
const pausedWelcome = new Map();
let marketingQueued = false;

function scheduleCartMarketing() {
  if (marketingQueued) return;
  marketingQueued = true;
  queueMicrotask(syncCartMarketing);
}

function syncCartMarketing() {
  marketingQueued = false;
  const paused = /(?:^|\/)cart\/?$/.test(location.pathname) || Boolean(
    document.querySelector('.aiovo-cart-summary--page, .aiovo-cart-empty--page, #cart-drawer[open]')
  );
  const html = document.documentElement;
  if (html.hasAttribute('data-aiovo-cart-marketing-paused') !== paused)
    html.toggleAttribute('data-aiovo-cart-marketing-paused', paused);
  const roots = new Set([...document.querySelectorAll(welcomeForm)].map(
    (form) => form.closest('[data-kl-scroll-locking-modal]')
  ).filter(Boolean));
  document.querySelectorAll(welcomeTeaser).forEach((node) => roots.add(node));
  for (const [node, original] of pausedWelcome) {
    if (paused && node.isConnected && roots.has(node)) continue;
    node.removeAttribute('data-aiovo-cart-welcome-paused');
    if (node.getAttribute('inert') === '') {
      if (original.inert === null) node.removeAttribute('inert');
      else node.setAttribute('inert', original.inert);
    }
    if (node.getAttribute('aria-hidden') === 'true') {
      if (original.ariaHidden === null) node.removeAttribute('aria-hidden');
      else node.setAttribute('aria-hidden', original.ariaHidden);
    }
    pausedWelcome.delete(node);
  }
  if (paused) for (const node of roots) {
    if (!pausedWelcome.has(node)) pausedWelcome.set(node, {
      inert: node.getAttribute('inert'), ariaHidden: node.getAttribute('aria-hidden'),
    });
    const containedFocus = node.contains(document.activeElement);
    if (!node.hasAttribute('data-aiovo-cart-welcome-paused')) node.setAttribute('data-aiovo-cart-welcome-paused', '');
    if (!node.inert) node.inert = true;
    if (node.getAttribute('aria-hidden') !== 'true') node.setAttribute('aria-hidden', 'true');
    if (containedFocus) {
      const target = document.querySelector('#cart-drawer[open] button, main');
      if (target) {
        const tabindex = target.getAttribute('tabindex');
        if (tabindex === null) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        if (tabindex === null) target.removeAttribute('tabindex');
      } else document.activeElement?.blur();
    }
  }
  // Override only this application's dedicated body class, only when all its
  // locking modals belong to the paused welcome form. Other modal locks survive.
  const locking = [...document.querySelectorAll('[data-kl-scroll-locking-modal="true"]')];
  const unlock = paused && locking.length > 0 && locking.every((node) => roots.has(node));
  if (html.hasAttribute('data-aiovo-cart-welcome-lock-only') !== unlock)
    html.toggleAttribute('data-aiovo-cart-welcome-lock-only', unlock);
}
window.addEventListener('pageshow', syncCartMarketing);
document.addEventListener('aiovo:cart-render-end', syncCartMarketing);
syncCartMarketing();
